import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TrendingDown, CheckCircle2, AlertTriangle, MessageCircle, RotateCcw, Award, Info, Lock, Sparkles } from 'lucide-react';
import { fmtBRL } from './calcUtils';

const WHATSAPP_CALC_URL = 'https://wa.me/5595991548020?text=' + encodeURIComponent('Olá! Usei a Calculadora de Impostos do site da Apex Medical Hub e gostaria de agendar uma conversa com o time para reduzir meus impostos.');
const WHATSAPP_FATOR_R_URL = 'https://wa.me/5595991548020?text=' + encodeURIComponent('Olá! Vi na calculadora que o Anexo III poderia ser vantajoso para mim, mas depende de ajuste na folha (Fator R). Gostaria de conversar com o time da Apex para avaliar.');

const SavingsBanner = ({ results }) => {
  if (results.savings_monthly > 0) {
    return (
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white text-center" data-testid="calc-savings-banner">
        <TrendingDown className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm uppercase tracking-wider text-white/80 mb-1">Economia potencial identificada</p>
        <p className="font-heading text-3xl md:text-4xl font-bold">{fmtBRL(results.savings_monthly)}/mês</p>
        <p className="text-white/90 mt-1">Isso representa <strong>{fmtBRL(results.savings_annual)} por ano</strong> migrando para o regime mais econômico.</p>
      </div>
    );
  }
  if (results.current_key) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-center" data-testid="calc-no-savings-banner">
        <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-blue-600" />
        <p className="font-semibold text-slate-900">Você já está no regime mais econômico para o seu perfil!</p>
        <p className="text-sm text-slate-600 mt-1">Ainda assim, uma análise detalhada pode revelar outras oportunidades de economia.</p>
      </div>
    );
  }
  return null;
};

const AlcBanner = ({ results }) => {
  if (!results.alc) return null;
  return (
    <div
      className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50 p-4"
      data-testid="calc-alc-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center">
          <Sparkles className="w-4.5 h-4.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 text-sm md:text-base">
            Benefício aplicado: Área de Livre Comércio de Boa Vista
          </p>
          <p className="text-xs md:text-sm text-slate-700 mt-0.5 leading-relaxed">
            Sua empresa está localizada em zona com isenção de <strong>PIS e COFINS</strong>.
            No Lucro Presumido essa economia chega a <strong>3,65% do faturamento</strong>. Simulação já aplicada nos cenários abaixo.
          </p>
        </div>
      </div>
    </div>
  );
};

const RecommendedHero = ({ scenario }) => {
  if (!scenario) return null;
  return (
    <div
      className="relative rounded-2xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 p-6 md:p-7 shadow-lg shadow-emerald-100"
      data-testid="calc-recommended-hero"
    >
      <div className="absolute -top-3 left-6">
        <div className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md">
          <Award className="w-3.5 h-3.5" />
          Recomendado para você
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-2">
        <div className="min-w-0 w-full md:flex-1">
          <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1">Regime mais econômico e elegível</p>
          <p className="font-heading text-xl md:text-2xl font-bold text-slate-900 leading-tight break-words">{scenario.name}</p>
          {scenario.alc_benefit && (
            <div className="inline-flex items-center gap-1 mt-1.5 bg-indigo-100 text-indigo-800 text-[11px] font-semibold px-2 py-0.5 rounded-full">
              <Sparkles className="w-3 h-3" />
              Zona Franca • PIS/COFINS isento
            </div>
          )}
          <p className="text-sm text-slate-600 mt-1">Carga tributária efetiva de <strong>{scenario.effective_rate}%</strong> sobre o faturamento.</p>
        </div>
        <div className="text-left md:text-right flex-shrink-0">
          <p className="text-xs text-slate-500">Você pagaria</p>
          <p className="font-heading text-3xl md:text-4xl font-extrabold text-emerald-700 leading-none whitespace-nowrap">
            {fmtBRL(scenario.monthly_total)}
            <span className="text-sm font-normal text-slate-500">/mês</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">{fmtBRL(scenario.annual_total)}/ano</p>
        </div>
      </div>
      {scenario.breakdown?.length > 0 && (
        <details className="mt-4 pt-4 border-t border-emerald-200">
          <summary className="text-xs text-emerald-700 cursor-pointer font-semibold">Ver composição dos tributos</summary>
          <ul className="mt-3 space-y-1.5">
            {scenario.breakdown.map((b, i) => (
              <li key={i} className="flex justify-between gap-2 text-xs text-slate-700">
                <span className="min-w-0 break-words">{b.label}</span>
                <span className="font-medium whitespace-nowrap flex-shrink-0">{fmtBRL(b.value, 2)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const FatorRAlert = ({ results }) => {
  // Só exibe quando faz sentido: Fator R abaixo de 28% e o cliente NÃO é o pessoa física / equiparação
  const anexo3 = results.scenarios.find((s) => s.key === 'simples_anexo_iii');
  if (!anexo3 || anexo3.eligible) return null;
  if (results.fator_r_pct >= 28) return null;
  // Só mostra se Anexo III seria financeiramente atrativo (mais barato que o recomendado atual)
  const best = results.scenarios.find((s) => s.key === results.best_key);
  if (!best || anexo3.monthly_total === null || anexo3.monthly_total >= best.monthly_total) return null;

  return (
    <div
      className="rounded-xl border border-amber-300 bg-amber-50 p-5"
      data-testid="calc-fator-r-alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 text-sm md:text-base">
            Atenção: o Anexo III parece mais barato, mas você não se enquadra hoje
          </p>
          <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">
            O <strong>Simples Nacional — Anexo III</strong> só é permitido quando a folha de pagamento representa <strong>pelo menos 28% do faturamento</strong> (regra do Fator R).
            Hoje sua folha equivale a <strong>{results.fator_r_pct}%</strong> — por isso o Anexo III <strong>não é uma opção legal para você agora</strong>.
          </p>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            <strong>Boa notícia:</strong> na maioria dos casos, um ajuste estratégico de pró-labore permite atingir o Fator R e migrar para o Anexo III com segurança.
            Nosso time avalia isso gratuitamente.
          </p>
          <Button
            asChild
            size="sm"
            className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-md w-full sm:w-auto whitespace-normal h-auto py-2 text-left"
            data-testid="calc-fator-r-cta"
          >
            <a href={WHATSAPP_FATOR_R_URL} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-1.5 h-4 w-4 flex-shrink-0" />
              Avaliar meu enquadramento no Fator R
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

const ScenarioRow = ({ scenario, isCurrent }) => {
  const isPotencial = !scenario.eligible && scenario.monthly_total !== null;
  return (
    <div
      className={`rounded-lg border p-4 transition-colors ${
        isPotencial
          ? 'border-slate-200 bg-slate-50/70'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`calc-scenario-${scenario.key}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
        <div className="min-w-0 w-full sm:flex-1">
          <div className="flex items-start gap-1.5 min-w-0">
            {isPotencial && <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-1" />}
            <p className={`font-semibold text-sm md:text-base break-words min-w-0 flex-1 ${isPotencial ? 'text-slate-500' : 'text-slate-900'}`}>
              {scenario.name}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {isCurrent && (
              <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50 text-[10px]">
                Seu regime atual
              </Badge>
            )}
            {scenario.alc_benefit && !isPotencial && (
              <Badge className="bg-indigo-100 hover:bg-indigo-100 text-indigo-800 text-[10px] gap-0.5">
                <Sparkles className="w-2.5 h-2.5" />
                PIS/COFINS isento
              </Badge>
            )}
            {isPotencial && (
              <Badge className="bg-slate-200 hover:bg-slate-200 text-slate-700 text-[10px]">
                Potencial — não aplicável hoje
              </Badge>
            )}
          </div>
          {scenario.note && (
            <p className={`text-xs mt-1.5 flex items-start gap-1 ${isPotencial ? 'text-slate-500' : 'text-amber-700'}`}>
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{scenario.note}</span>
            </p>
          )}
        </div>
        <div className="text-left sm:text-right flex-shrink-0">
          {scenario.monthly_total !== null ? (
            <>
              <p className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap ${isPotencial ? 'text-slate-400 line-through decoration-slate-400' : 'text-slate-900'}`}>
                {fmtBRL(scenario.monthly_total)}
                <span className="text-xs font-normal text-slate-500 no-underline">/mês</span>
              </p>
              <p className={`text-xs ${isPotencial ? 'text-slate-400' : 'text-slate-500'}`}>
                {scenario.effective_rate}% do faturamento
              </p>
            </>
          ) : (
            <p className="font-semibold text-slate-400 text-sm">Não elegível</p>
          )}
        </div>
      </div>
      {scenario.breakdown?.length > 0 && !isPotencial && (
        <details className="mt-2">
          <summary className="text-xs text-[#007f73] cursor-pointer font-medium">Ver composição dos tributos</summary>
          <ul className="mt-2 space-y-1">
            {scenario.breakdown.map((b, i) => (
              <li key={i} className="flex justify-between gap-2 text-xs text-slate-600">
                <span className="min-w-0 break-words">{b.label}</span>
                <span className="whitespace-nowrap flex-shrink-0">{fmtBRL(b.value, 2)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export const CalculatorResults = ({ results, onRestart }) => {
  const best = results.scenarios.find((s) => s.key === results.best_key);
  const others = results.scenarios.filter((s) => s.key !== results.best_key);

  // Reordena: elegíveis primeiro (regime atual no topo se houver), potenciais/não elegíveis por último
  const eligibleOthers = others.filter((s) => s.eligible && s.monthly_total !== null);
  const potentialOthers = others.filter((s) => !s.eligible || s.monthly_total === null);

  // Coloca o regime atual no topo dos elegíveis (se não for o recomendado)
  eligibleOthers.sort((a, b) => {
    if (a.key === results.current_key) return -1;
    if (b.key === results.current_key) return 1;
    return (a.monthly_total ?? Infinity) - (b.monthly_total ?? Infinity);
  });

  return (
    <div className="space-y-5 min-w-0" data-testid="calculator-results">
      <SavingsBanner results={results} />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700">
          Fator R: <strong>{results.fator_r_pct}%</strong> → Anexo {results.anexo_aplicavel}
        </span>
        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700">
          ISS do município: <strong>{results.iss_rate_pct}%</strong>
        </span>
      </div>

      <AlcBanner results={results} />

      <RecommendedHero scenario={best} />

      <FatorRAlert results={results} />

      {eligibleOthers.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold pt-2">
            Outros cenários elegíveis
          </p>
          {eligibleOthers.map((s) => (
            <ScenarioRow key={s.key} scenario={s} isCurrent={s.key === results.current_key} />
          ))}
        </div>
      )}

      {potentialOthers.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold pt-2">
            Cenários potenciais (não aplicáveis ao seu perfil hoje)
          </p>
          {potentialOthers.map((s) => (
            <ScenarioRow key={s.key} scenario={s} isCurrent={s.key === results.current_key} />
          ))}
          <p className="text-xs text-slate-500 leading-relaxed italic">
            Estes regimes aparecem apenas como referência — atualmente eles <strong>não podem ser aplicados legalmente</strong> ao seu caso.
            Alguns podem se tornar viáveis mediante planejamento tributário (ex.: ajuste de folha para o Fator R).
          </p>
        </div>
      )}

      <p className="text-xs text-slate-500 leading-relaxed">
        * Valores estimados com base nas tabelas vigentes (Simples Nacional, IRPF 2026, IRPJ/CSLL/PIS/COFINS do Lucro Presumido e ISS municipal).
        A simulação não substitui uma análise tributária profissional — cada caso possui particularidades que podem alterar o resultado.
      </p>

      <details className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="calc-scope-details">
        <summary className="text-xs font-semibold text-slate-700 cursor-pointer select-none">
          Escopo da simulação — o que está incluído e o que fica de fora
        </summary>
        <div className="mt-3 text-xs text-slate-600 space-y-2 leading-relaxed">
          <p>
            <strong className="text-slate-800">Incluído:</strong> IRPJ, CSLL, PIS, COFINS, ISS e INSS Patronal (20% da folha) para
            regimes de PJ; DAS unificado para Simples Nacional; IRPF (Carnê-Leão) e INSS contribuinte individual para Pessoa Física.
          </p>
          <p>
            <strong className="text-slate-800">Não incluído:</strong> IRRF sobre pró-labore, IRPF do sócio sobre distribuição de lucros
            (isento na PJ), taxas municipais/CRM/ANVISA, honorários contábeis, obrigações acessórias, PIS/COFINS-Importação e demais
            tributos não incidentes diretamente sobre a receita de serviços médicos.
          </p>
          <p>
            <strong className="text-slate-800">Regras específicas aplicadas:</strong> Fator R (28%) para Anexo III do Simples;
            base presumida reduzida (IRPJ 8% e CSLL 12%) na Equiparação Hospitalar; ISS fixo por sócio na SUP;
            isenção de PIS e COFINS na Área de Livre Comércio de Boa Vista - RR.
          </p>
        </div>
      </details>

      <div className="bg-slate-900 rounded-xl p-6 text-center">
        <p className="font-heading text-lg font-bold text-white mb-1">Quer transformar essa economia em realidade?</p>
        <p className="text-slate-400 text-sm mb-4">Nosso time de especialistas em tributação médica analisa seu caso gratuitamente e cuida de toda a migração com segurança.</p>
        <Button asChild className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-8 py-6 text-base font-semibold" data-testid="calc-schedule-button">
          <a href={WHATSAPP_CALC_URL} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="mr-2 h-5 w-5" />
            Agendar conversa com nosso time
          </a>
        </Button>
        <button onClick={onRestart} className="flex items-center gap-1 text-slate-400 hover:text-white text-xs mt-4 mx-auto transition-colors" data-testid="calc-restart-button">
          <RotateCcw className="w-3 h-3" />
          Fazer nova simulação
        </button>
      </div>
    </div>
  );
};
